import React, { useState } from 'react';
import type { SitrepSectionType } from '../DisasterGPT/hooks/useSitrepsV2';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import ActionsTable from '../Actions/ActionsTable';
import EditOrgActionsModal from '../Actions/EditOrgActionsModal';
import { ORGANIZATION } from '../IAP/ICSConstants';
import type { GetOrgActionResponse } from '../Actions/hooks/orgActionHooks';
import { SharedIcon } from '../SharedIcon/SharedIcon';

/** System section template id of the OPORD "Tasks" section. */
export const TASKS_SECTION_TEMPLATE_ID = 'b0d00011-0000-0000-0000-000000000011';

export const isTasksSection = (
  section: Pick<SitrepSectionType, 'section_template_id'>
): boolean => section.section_template_id === TASKS_SECTION_TEMPLATE_ID;

type ReportTasksActionsProps = {
  /** `item_reference` id of the report; actions are linked to and listed by it. */
  reportItemId: UUID;
  canEdit: boolean;
};

/**
 * Actions linked to a report, shown under its Tasks section. Independent of the
 * section text, so AI generating or editing the text never touches these rows.
 */
const ReportTasksActions: React.FC<ReportTasksActionsProps> = ({
  reportItemId,
  canEdit,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedAction, setSelectedAction] =
    useState<GetOrgActionResponse | null>(null);

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-center pb-2">
        <div className="txt-md">Actions</div>
        {canEdit && (
          <StylishNewButton
            className="button--primary"
            onClick={() => setShowAddModal(true)}
          >
            <SharedIcon iconName="add" classes="me-2" />
            Add Action
          </StylishNewButton>
        )}
      </div>

      <ActionsTable
        scope={ORGANIZATION}
        itemId={reportItemId}
        onEditAction={canEdit ? setSelectedAction : undefined}
      />

      {showAddModal && (
        <EditOrgActionsModal
          linkedItemId={reportItemId}
          toggle={() => setShowAddModal(false)}
        />
      )}
      {selectedAction && (
        <EditOrgActionsModal
          selectedEntity={selectedAction}
          toggle={() => setSelectedAction(null)}
        />
      )}
    </div>
  );
};

export default ReportTasksActions;
